import { Button, Callout } from "@blueprintjs/core";
import { format } from "date-fns";
import { FieldSet } from "nsitools-react";
import * as React from "react";
import styled from "styled-components";

import { CarouselComparer, CO_FINAL_KEY, DoublonsMultipleDataCard, useDoublonsStateContext } from "..";
import { ContactDoublonEditDto, ContactDoublonSiegeSocialDto } from "../../../../../api";
import { ERoutes } from "../../../../../AppRouter";
import { BooleanColumn, ViewButton } from "../../../../../components";
import { useTl } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";

const Container = styled.div`
  height: 70vh;
  overflow: auto;
  display: flex;
  flex-direction: column;
`;

const InformationContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

export interface IDoublonContactSiegesSociauxProps {}

export const DoublonContactSiegesSociaux: React.FunctionComponent<IDoublonContactSiegesSociauxProps> = props => {
  const { t } = useTl();
  const { data, selectData, getSelectedIndex } = useDoublonsStateContext();

  const selectedIndex = React.useMemo(() => getSelectedIndex(`${CO_FINAL_KEY}.idcontact`) ?? 0, [getSelectedIndex]);
  const contactContainer = React.useCallback(
    (index: number, nb: number) => {
      const contact: ContactDoublonEditDto = data?.contacts[index];
      if (!contact) return;

      return (
        <FieldSet
          key={contact.idcontact}
          title={
            <TitleContainer>
              {index !== selectedIndex ? (
                <Button
                  text={t(ETLCodes.Conserver)}
                  icon={"arrow-left"}
                  onClick={() => selectData(`${CO_FINAL_KEY}.idcontact`, index)}
                />
              ) : (
                <div />
              )}
              <ViewButton
                text={t(ETLCodes.ContactNb, { nb: nb, id: contact.idcontact })}
                onClick={() => window.open(`#${ERoutes.contacts}/${contact.idcontact}/detail/view`, "_blank")}
              />
              <div />
            </TitleContainer>
          }
          titlePosition="center"
        >
          <DoublonsMultipleDataCard
            labelPredicate={(s: ContactDoublonSiegeSocialDto) => s.denominationSociale}
            fieldPath={`contacts[${index}].siegesSociaux`}
            interactive={() => false}
            forceSelected={() => false}
            finalEntityKey={CO_FINAL_KEY}
          >
            {(s: ContactDoublonSiegeSocialDto) => (
              <InformationContainer key={s.idcontactSiege}>
                <div>
                  {t(ETLCodes.NumeroBce)}: {s.numeroBce}
                </div>
                <div>
                  {t(ETLCodes.Fonction)}: {s.fonction}
                </div>
                <div>
                  {t(ETLCodes.Telephone)}: {s.telephone?.numero}
                </div>
                <div>
                  {t(ETLCodes.Gsm)}: {s.gsm?.numero}
                </div>
                <div>
                  {t(ETLCodes.Email)}: {s.email?.adresseEmail}
                </div>
                <div>
                  {t(ETLCodes.Remarque)}: {s.remarqueSiege}
                </div>
                <div>
                  {t(ETLCodes.PersonneContactPrincipale)}: <BooleanColumn value={s.principal} />
                </div>
                <div>
                  {t(ETLCodes.SortieEntreprise)}: {s.sortieEntreprise && format(s.sortieEntreprise, "dd-MM-yyyy")}
                </div>
              </InformationContainer>
            )}
          </DoublonsMultipleDataCard>
        </FieldSet>
      );
    },
    [data?.contacts, selectData, selectedIndex, t]
  );

  return (
    <Container>
      <Callout
        intent="warning"
        title={t(ETLCodes.DoublonContactSiegeSocialCumules)}
        style={{ marginBottom: "1rem" }}
        icon="info-sign"
      />
      <CarouselComparer leftElement={contactContainer(selectedIndex, selectedIndex + 1)}>
        {data?.contacts?.length > 1 &&
          data?.contacts
            ?.filter((a, i) => i !== selectedIndex)
            ?.map((a, i) => contactContainer(i + (i >= selectedIndex ? 1 : 0), i + (i >= selectedIndex ? 2 : 1)))}
      </CarouselComparer>
    </Container>
  );
};
